import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ProfileMenu, type SidebarAuth } from './ProfileMenu';
import { LangToggle } from './LangToggle';

// The clickable brand at the top of the rail: a wordmark linking somewhere.
export interface SidebarBrand {
  to: string;
  label: string;
}

// Left rail for the NoticeIA app.
//   - top: brand wordmark, centered;
//   - profile: avatar + name; click for a menu (Sign out) — or a sign-in button
//     when logged out;
//   - children: the app's nav tabs;
//   - bottom: the FR/EN language toggle (via `langToggle`), over the global light/dark toggle — pinned here so every
//     sidebar/drawer carries them by construction.
// The rail stays dark in both themes (dark sidebar + light content). The `dark`
// class forces a dark context, so themeable children (ProfileMenu) render dark
// here even when the page is in light mode.
export function AppSidebar({
  className = '',
  children,
  langToggle = false,
  brand,
  auth,
  foot,
}: {
  className?: string;
  children?: ReactNode;
  // Show the FR/EN toggle above the theme toggle.
  langToggle?: boolean;
  // The top-left brand link: the wordmark alone.
  brand: SidebarBrand;
  // The NoticeIA profile session.
  auth: SidebarAuth;
  // At the bottom, over the language toggle.
  foot?: ReactNode;
}) {

  return (
    <aside className={`dark w-56 shrink-0 flex-col px-3 py-5 text-slate-100 ${className}`}>
      <Link
        to={brand.to}
        aria-label={`${brand.label} home`}
        className="mb-6 flex items-center justify-center gap-2 transition-opacity hover:opacity-80"
      >
        <span className="text-lg font-bold tracking-wide">{brand.label}</span>
      </Link>

      <div className="mb-5">
        <ProfileMenu showName auth={auth} />
      </div>

      {children}

      <div className="mt-auto flex flex-col items-center gap-2 pt-4">
        {langToggle && <LangToggle />}
        {foot}
      </div>
    </aside>
  );
}
